/**
 * Sidebar navigation component.
 */

import { FC } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Router as SwitchIcon,
  Lan as VlanIcon,
  Cable as InterfaceIcon,
  CheckCircle as ConnectivityIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  SupervisorAccount as UserManagementIcon,
} from '@mui/icons-material';
import { useUIStore } from '@/stores/uiStore';
import { useIsAdmin } from '@/stores/authStore';

const DRAWER_WIDTH = 240;

interface NavItem {
  text: string;
  icon: JSX.Element;
  path: string;
}

const navItems: NavItem[] = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'Switches', icon: <SwitchIcon />, path: '/switches' },
  { text: 'VLANs', icon: <VlanIcon />, path: '/vlans' },
  { text: 'Interfaces', icon: <InterfaceIcon />, path: '/interfaces' },
  { text: 'Connectivity Check', icon: <ConnectivityIcon />, path: '/connectivity' },
  { text: 'Transaction History', icon: <HistoryIcon />, path: '/transactions' },
];

const settingsNavItems: NavItem[] = [
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
];

const adminNavItems: NavItem[] = [
  { text: 'User Management', icon: <UserManagementIcon />, path: '/admin/users' },
];

export const Sidebar: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarOpen } = useUIStore();
  const isAdmin = useIsAdmin();

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <Drawer
      variant="persistent"
      open={sidebarOpen}
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
        },
      }}
    >
      <Toolbar />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigate(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        {settingsNavItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigate(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Admin-only menu items */}
      {isAdmin && (
        <>
          <Divider />
          <List>
            {adminNavItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => handleNavigate(item.path)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Drawer>
  );
};
